import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import { useGetStreakStatistics } from "../../generated/hook";
import StreakBadge from "../../components/StreakBadge";
import MilestoneBadges from "../../components/MilestoneBadges";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const StreakStatisticsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    data: streakStats,
    isLoading: statsLoading,
    error: statsError,
  } = useGetStreakStatistics();

  if (authLoading || statsLoading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    navigate("/signin");
    return null;
  }

  if (statsError) {
    return (
      <Container sx={{ mt: 5 }}>
        <Alert severity="error">
          {t("streaks.errors.loadStats")}:{" "}
          {(statsError as unknown as Error)?.message ||
            t("streaks.errors.unknown")}
        </Alert>
      </Container>
    );
  }

  if (!streakStats) {
    return (
      <Container sx={{ mt: 5 }}>
        <Alert severity="info">{t("streaks.noData")}</Alert>
      </Container>
    );
  }

  const {
    activeStreaksCount,
    longestCurrentStreak,
    allTimeLongestStreak,
    averageStreakLength,
    habitStreaks,
  } = streakStats;

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("streaks.pageTitle")}
      </Typography>

      {/* Overview Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CheckCircleIcon sx={{ mr: 1, color: "success.main" }} />
              <Typography variant="h6" component="div">
                {t("streaks.overview.activeStreaks.title")}
              </Typography>
            </Box>
            <Typography variant="h3" component="div" color="success.main">
              {activeStreaksCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("streaks.overview.activeStreaks.subtitle")}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocalFireDepartmentIcon sx={{ mr: 1, color: "error.main" }} />
              <Typography variant="h6" component="div">
                {t("streaks.overview.bestCurrent.title")}
              </Typography>
            </Box>
            <Typography variant="h3" component="div" color="error.main">
              {longestCurrentStreak}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("streaks.overview.bestCurrent.subtitle")}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <EmojiEventsIcon sx={{ mr: 1, color: "warning.main" }} />
              <Typography variant="h6" component="div">
                {t("streaks.overview.allTimeBest.title")}
              </Typography>
            </Box>
            <Typography variant="h3" component="div" color="warning.main">
              {allTimeLongestStreak}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("streaks.overview.allTimeBest.subtitle")}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TrendingUpIcon sx={{ mr: 1, color: "info.main" }} />
              <Typography variant="h6" component="div">
                {t("streaks.overview.averageStreak.title")}
              </Typography>
            </Box>
            <Typography variant="h3" component="div" color="info.main">
              {averageStreakLength.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("streaks.overview.averageStreak.subtitle")}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Habits Table */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t("streaks.table.title")}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("streaks.table.columns.habit")}</TableCell>
                <TableCell align="center">
                  {t("streaks.table.columns.currentStreak")}
                </TableCell>
                <TableCell align="center">
                  {t("streaks.table.columns.longestStreak")}
                </TableCell>
                <TableCell align="center">
                  {t("streaks.table.columns.milestones")}
                </TableCell>
                <TableCell align="center">
                  {t("streaks.table.columns.status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {habitStreaks.length > 0 ? (
                habitStreaks.map((habit) => (
                  <TableRow key={habit.habitId}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body1" fontWeight={500}>
                        {habit.habitTitle}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <StreakBadge
                        currentStreak={habit.currentStreak}
                        longestStreak={habit.longestStreak}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {habit.longestStreak} {t("streaks.table.days")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <MilestoneBadges
                        currentStreak={habit.currentStreak}
                        longestStreak={habit.longestStreak}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {habit.isActive ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: "success.light",
                            color: "success.dark",
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                          <Typography variant="caption" fontWeight={600}>
                            {t("streaks.table.status.active")}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          {t("streaks.table.status.inactive")}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3 }}
                    >
                      {t("streaks.table.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default StreakStatisticsPage;
